import type { ButtonLabelSource } from './domainEvents';

/**
 * Resolves a stable tracking id for a button click.
 *
 * Prefer an explicit `trackingId` from the call site. When none is provided,
 * fall back to slugified `buttonText`, `tooltip`, or `name`. The old IconButton
 * default `buttonText` of `'icon button'` is treated as absent so we never
 * emit an anonymous event. `Button` passes no `buttonText`, so its fallbacks
 * are `tooltip` and `name`.
 *
 * Note: a `Button` that opens a confirmation emits `button_clicked` on the click
 * itself, before the dialog. The confirmed action is already covered by domain
 * events such as `featureActionUsed` / `reportCreated`.
 *
 * Note: `Button` with a `to` prop renders a link. It sends a click event only for an
 * explicit `trackingId`; without one the navigation is covered by `$pageview` alone.
 */
export const ICON_BUTTON_DEFAULT_TEXT = 'icon button';

const MAX_TRACKING_ID_LENGTH = 80;

export interface ButtonTrackingInput {
    trackingId?: string;
    buttonText?: string;
    tooltip?: string;
    name?: string;
}

export interface ResolvedButtonTracking {
    trackingId: string;
    labelSource: ButtonLabelSource;
}

function usableLabel(value: string | undefined, treatAsAbsent?: string): string | null {
    const trimmed = value?.trim();
    if (!trimmed) {
        return null;
    }

    if (treatAsAbsent && trimmed.toLowerCase() === treatAsAbsent.toLowerCase()) {
        return null;
    }

    return trimmed;
}

export function slugifyTrackingId(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/['"]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, MAX_TRACKING_ID_LENGTH);
}

export function resolveButtonTracking(input: ButtonTrackingInput): ResolvedButtonTracking | null {
    const explicit = usableLabel(input.trackingId);
    if (explicit) {
        return { trackingId: explicit, labelSource: 'explicit' };
    }

    const buttonText = usableLabel(input.buttonText, ICON_BUTTON_DEFAULT_TEXT);
    if (buttonText) {
        const trackingId = slugifyTrackingId(buttonText);
        if (trackingId) {
            return { trackingId, labelSource: 'buttonText' };
        }
    }

    const tooltip = usableLabel(input.tooltip);
    if (tooltip) {
        const trackingId = slugifyTrackingId(tooltip);
        if (trackingId) {
            return { trackingId, labelSource: 'tooltip' };
        }
    }

    const name = usableLabel(input.name);
    if (name) {
        const trackingId = slugifyTrackingId(name);
        if (trackingId) {
            return { trackingId, labelSource: 'name' };
        }
    }

    return null;
}
